import {
  ClassNameMap,
  TypographyClasses,
  TypographyProps,
} from "@mui/material";
import React, { HTMLAttributes } from "react";
import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import { getValueOrBlank, isEmpty, isNotEmpty } from "../../helpers/utils";
import { ChipOptionsDataType } from "../chip/MuiChip";
import MuiChipWithPopover from "../chipWithPopover/MuiChipWithPopover";
import { MuiTypography } from "../typography/MuiTypography";
import { MuiDoubleLabelWithChipWrapper } from "./MuiDoubleLabelWithChip.style";
import { EMPTY_VALUE_DASH } from "../../helpers/constants";

export interface MuiDoubleLabelWithChipProps
  extends HTMLAttributes<HTMLDivElement> {
  label: string;
  value: ChipOptionsDataType[];
  labelTypoVariant?: TypographyProps["variant"];
  valueTypoVariant?: TypographyProps["variant"];
  labelClasses?: Partial<TypographyClasses> & Partial<ClassNameMap<never>>;
  valueClasses?: Partial<TypographyClasses> & Partial<ClassNameMap<never>>;
  isValueWithEllipsis?: boolean;
  replaceEmptyWithDash?: boolean;
  labelIcon?: React.ReactNode;
  chipPopoverHeaderLabel?: string;
  chipPopoverGridRef?: React.RefObject<HTMLElement>;
  isDialogTitleWithEllipsis?: boolean;
  countToWrap?: number;
}

export interface MuiDoubleLabelWithChipARIAProps {
  showMoreARIALabel: string;
  removeChipNameARIALabel: string;
  closePopoverARIALabel: string;
}

export const MuiDoubleLabelWithChip: React.FC<
  MuiDoubleLabelWithChipProps & MuiDoubleLabelWithChipARIAProps
> = ({
  label,
  value,
  labelTypoVariant = "h3",
  valueTypoVariant = "h4Bold",
  labelClasses,
  valueClasses,
  isValueWithEllipsis = false,
  replaceEmptyWithDash = false,
  labelIcon,
  chipPopoverHeaderLabel,
  chipPopoverGridRef,
  showMoreARIALabel,
  removeChipNameARIALabel,
  closePopoverARIALabel,
  isDialogTitleWithEllipsis = false,
  countToWrap,
  ...rest
}) => {
  let displayValue: string | ChipOptionsDataType[] = value;
  if (replaceEmptyWithDash) {
    displayValue = isEmpty(value) ? EMPTY_VALUE_DASH : value;
  }

  return (
    <MuiDoubleLabelWithChipWrapper {...rest}>
      <MuiTypography
        component={"h3"}
        data-testid={COMPONENT_IDS.COMMON_DOUBLE_LABEL}
        className="label-style"
        variant={labelTypoVariant}
        classes={labelClasses}
      >
        {label} {labelIcon}
      </MuiTypography>
      {isNotEmpty(value) ? (
        <MuiChipWithPopover
          data={value || []} //as ChipOptionsDataType[]
          headerLabel={getValueOrBlank(chipPopoverHeaderLabel)}
          gridRef={chipPopoverGridRef ?? React.createRef<HTMLElement>()}
          isEditable={false}
          showMoreARIALabel={showMoreARIALabel}
          removeChipNameARIALabel={removeChipNameARIALabel}
          closePopoverARIALabel={closePopoverARIALabel}
          isDialogTitleWithEllipsis={isDialogTitleWithEllipsis}
          countToWrap={countToWrap}
        />
      ) : (
        <MuiTypography
          component={"h4"}
          data-testid={COMPONENT_IDS.COMMON_DOUBLE_LABEL_VALUE}
          className="value-style"
          variant={valueTypoVariant}
          classes={valueClasses}
        >
          {displayValue as string}
        </MuiTypography>
      )}
    </MuiDoubleLabelWithChipWrapper>
  );
};
